/**
 * Everything the builder's palette offers, in the order it offers it (R4).
 *
 * ONE list, because there are four ways to reach it now: the sidebar's tile
 * grid a desktop drags from, the "/" picker, the phone's "+" sheet, and the
 * Outline's "+ Add field". They all read this array, so a new field type
 * appears everywhere or nowhere. The groups are the handoff's, mapped onto the
 * real 27 types (26 input types plus the NPS preset): "Multi-select" in the
 * design IS the real `multiselect` (Checkboxes) and gets one tile, the single
 * Checkbox stays, and Time, Date & time, Card and Widget are kept even though
 * the prototype omits them.
 *
 * `gated` is an entry that counts toward the plan's element cap. A Section, a
 * Card and a Page break only arrange what is already there, and a content
 * block asks nothing, so those stay live at the ceiling.
 */
import type { BInputType } from './builderModel';

export type DragPayload =
  | { kind: 'new-field'; type: BInputType; preset?: 'nps' }
  | { kind: 'new-section' } | { kind: 'new-card' } | { kind: 'new-row' }
  /** a section that starts a new page (R4: the Page break tile) */
  | { kind: 'new-page' }
  | { kind: 'new-widget' }
  | { kind: 'field'; key: string } | { kind: 'section'; id: string }
  /** an existing card, dragged to move or re-nest it */
  | { kind: 'card'; sec: string; id: string }
  /** an existing container, dragged to re-nest or promote it */
  | { kind: 'wnode'; sec: string; scope: string };

export type PaletteGroup = 'Layout' | 'Content' | 'Basic' | 'Choice' | 'Date & time' | 'Composite' | 'Advanced';
export const PALETTE_GROUPS: PaletteGroup[] = ['Layout', 'Content', 'Basic', 'Choice', 'Date & time', 'Composite', 'Advanced'];

export interface PaletteEntry {
  group: PaletteGroup;
  payload: DragPayload;
  glyph: string;
  label: string;
  /** the one-line description the "/" picker shows */
  desc: string;
  /** other words a search should find it by */
  alt?: string;
  gated?: boolean;
}

const f = (type: BInputType, preset?: 'nps'): DragPayload => ({ kind: 'new-field', type, ...(preset ? { preset } : {}) });

export const PALETTE: PaletteEntry[] = [
  { group: 'Layout', payload: { kind: 'new-section' }, glyph: '▤', label: 'Section', desc: 'Groups fields under a title' },
  { group: 'Layout', payload: { kind: 'new-card' }, glyph: '▢', label: 'Card', desc: 'A box around related fields — cards nest', alt: 'group box' },
  { group: 'Layout', payload: { kind: 'new-widget' }, glyph: '⟳', label: 'Widget', desc: 'Repeats for every item the respondent adds', alt: 'repeating group repeat container', gated: true },
  { group: 'Layout', payload: { kind: 'new-page' }, glyph: '⤓', label: 'Page break', desc: 'Starts a new page here', alt: 'step next page' },
  { group: 'Content', payload: f('heading'), glyph: 'H', label: 'Heading', desc: 'A title between fields', alt: 'title' },
  { group: 'Content', payload: f('paragraph'), glyph: '¶', label: 'Paragraph', desc: 'Words the respondent reads', alt: 'text block explanation' },
  { group: 'Content', payload: f('image'), glyph: '▣', label: 'Image', desc: 'A picture on the form', alt: 'photo picture' },
  { group: 'Content', payload: f('divider'), glyph: '—', label: 'Divider', desc: 'A line across the form', alt: 'horizontal line rule' },
  { group: 'Basic', payload: f('text'), glyph: 'Aa', label: 'Short text', desc: 'One line of text', alt: 'text input string', gated: true },
  { group: 'Basic', payload: f('textarea'), glyph: '≣', label: 'Long text', desc: 'Several lines of text', alt: 'text area textarea paragraph answer', gated: true },
  { group: 'Basic', payload: f('email'), glyph: '@', label: 'Email', desc: 'An email address, checked as one', gated: true },
  { group: 'Basic', payload: f('tel'), glyph: '☎', label: 'Phone', desc: 'A phone number', alt: 'tel telephone', gated: true },
  { group: 'Basic', payload: f('number'), glyph: '#', label: 'Number', desc: 'A number, with limits if you want', alt: 'numeric', gated: true },
  { group: 'Basic', payload: f('url'), glyph: '⌁', label: 'URL', desc: 'A web address', alt: 'website link', gated: true },
  { group: 'Choice', payload: f('radio'), glyph: '◉', label: 'Single choice', desc: 'Pick one', alt: 'radio multiple choice', gated: true },
  { group: 'Choice', payload: f('multiselect'), glyph: '☑', label: 'Checkboxes', desc: 'Pick any', alt: 'multi-select multiselect multiple', gated: true },
  { group: 'Choice', payload: f('select'), glyph: '▾', label: 'Dropdown', desc: 'Pick one from a list — or a built-in state or country list', alt: 'select', gated: true },
  { group: 'Choice', payload: f('checkbox'), glyph: '✓', label: 'Checkbox', desc: 'A single tick — consent, yes or no', alt: 'consent agree tick', gated: true },
  { group: 'Choice', payload: f('rating'), glyph: '★', label: 'Rating', desc: 'Stars, up to ten', alt: 'stars', gated: true },
  { group: 'Choice', payload: f('scale'), glyph: '1‒5', label: 'Scale', desc: '1 to 5, or 0 to 10', alt: 'linear scale likert', gated: true },
  { group: 'Choice', payload: f('scale', 'nps'), glyph: '0‒10', label: 'NPS', desc: '0–10: how likely are you to recommend us', alt: 'net promoter score', gated: true },
  { group: 'Choice', payload: f('matrix'), glyph: '⊞', label: 'Matrix', desc: 'Rows that share one set of answers', alt: 'grid table', gated: true },
  { group: 'Choice', payload: f('ranking'), glyph: '⇅', label: 'Ranking', desc: 'Put items in order', alt: 'order sort', gated: true },
  { group: 'Date & time', payload: f('date'), glyph: '▦', label: 'Date', desc: 'A day', alt: 'calendar', gated: true },
  { group: 'Date & time', payload: f('time'), glyph: '◷', label: 'Time', desc: 'A time of day', alt: 'clock', gated: true },
  { group: 'Date & time', payload: f('datetime'), glyph: '▦◷', label: 'Date & time', desc: 'A day and a time', alt: 'datetime', gated: true },
  { group: 'Composite', payload: f('name'), glyph: '👤', label: 'Name', desc: 'First and last name', alt: 'person', gated: true },
  { group: 'Composite', payload: f('address'), glyph: '⌂', label: 'Address', desc: 'Street, city, region, country', alt: 'shipping postal', gated: true },
  { group: 'Advanced', payload: f('file'), glyph: '⇪', label: 'File upload', desc: 'One or more files', alt: 'attachment document', gated: true },
  { group: 'Advanced', payload: f('signature'), glyph: '✒', label: 'Signature', desc: 'Drawn or typed, with the terms they agree to', alt: 'sign esign', gated: true },
  { group: 'Advanced', payload: f('payment'), glyph: '💳', label: 'Payment', desc: 'Pay by card when they submit', alt: 'stripe charge price', gated: true },
];

/**
 * The entries matching a search, best first: a label that starts with the
 * words, then a label containing them, then the other words a person might
 * call it by. Empty search = everything in palette order.
 */
export function searchPalette(query: string, entries: PaletteEntry[] = PALETTE): PaletteEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  const rank = (e: PaletteEntry): number => {
    const label = e.label.toLowerCase();
    if (label.startsWith(q)) return 0;
    if (label.includes(q)) return 1;
    if ((e.alt || '').toLowerCase().includes(q) || e.desc.toLowerCase().includes(q)) return 2;
    return -1;
  };
  return entries
    .map((e, i) => ({ e, i, r: rank(e) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.e);
}

/** The tile a field of this type came from — its label and glyph for the inspector's type chip. */
export function paletteEntryFor(type: string, preset?: { min?: string; max?: string } | null): PaletteEntry | undefined {
  if (type === 'scale' && preset && preset.min === '0' && preset.max === '10') {
    return PALETTE.find((e) => e.payload.kind === 'new-field' && e.payload.preset === 'nps');
  }
  return PALETTE.find((e) => e.payload.kind === 'new-field' && e.payload.type === type && !e.payload.preset);
}
